import { useState, useEffect } from 'react';
import api from '../api/axiosInstance';
import { PipelineStats, PipelineSummaryStats, PipelineStatus } from '../const/PipelineTypes';
import keycloak from '../services/keycloak';
import { useWebSocketService } from './useWebSocketService';
import { useSwobNotifications } from './NotificationsProvider';
import { AxiosError } from 'axios';

export const usePipelineSummary = (): PipelineSummaryStats => {
  const [stats, setStats] = useState<PipelineStats>({
    [PipelineStatus.InProgress]: 0,
    [PipelineStatus.Success]: 0,
    [PipelineStatus.Failure]: 0,
    [PipelineStatus.Canceled]: 0,
    [PipelineStatus.Pending]: 0,
    [PipelineStatus.Completed]: 0,
    [PipelineStatus.Banned]: 0,
    [PipelineStatus.Manual]: 0,
    [PipelineStatus.Warning]: 0,
    [PipelineStatus.Aborted]: 0,
    [PipelineStatus.Paused]: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Connect to WebSocket with the JWT token
  const ws = useWebSocketService();
  const { addNotifications } = useSwobNotifications();

  useEffect(() => {
    const fetchWorkflowStats = async () => {
      try {
        setIsLoading(true);

        const resp = await api.get('/api/workflows/status');

        setStats(resp.data);
      } catch (error: unknown) {
        // show error
        addNotifications([
          {
            title: 'Error',
            icon: 'icon-triangle-warning',
            description:
              error && error instanceof AxiosError
                ? error.response?.data?.message || error.message
                : 'An error occurred while fetching pipeline summary',
            timestamp: new Date(),
            color: 'red'
          }
        ]);
        setError(error instanceof Error ? error.message : 'An error occurred while fetching the summary statuses of the pipelines');
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    };

    // Subscribe to workflow status summary changes
    const unsubscribe = ws?.onEntityChange('workflow_status_summary', (data) => {
      console.log('Received workflow status summary update:', data);

      // Handle error
      if (!data) {
        addNotifications([
          {
            title: 'Websocket Error',
            icon: 'icon-triangle-warning',
            description: 'An error occurred with the websocket while updating pipeline summary.',
            timestamp: new Date()
          }
        ]);
        return;
      }

      // Handle status change events
      if (data.action === 'status-change' && data.payload) {
        // Update stats with real-time data from WebSocket
        setStats(data.payload);
        console.log('Updated pipeline summary stats via WebSocket:', data.payload);
      }
    });

    // Initial data fetch
    if (keycloak.authenticated) {
      fetchWorkflowStats();
    }

    // Cleanup on component unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
        console.log('[usePipelineSummary] Cleaned up WebSocket listener');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    stats,
    isLoading,
    error
  };
};
